import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { CartItem } from "@/pages/store/hooks/useCart";
import { trackPurchase } from "@/lib/siteIntegrations";
import { useStoreProducts } from "@/hooks/useStoreProducts";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  cartItems: CartItem[];
  subtotal: number;
  onOrderComplete: (orderRef: string) => void;
}

const fmt = (n: number) => `KES ${Math.round(n).toLocaleString()}`;

type Step = "details" | "delivery" | "done";

interface Placed { ref: string; total: number; instructions: string; stk?: { ok: boolean; message?: string; error?: string } | null; phone: string; method: string }

export default function CheckoutModal({ isOpen, onClose, cartItems, subtotal, onOrderComplete }: Props) {
  const { settings } = useStoreProducts();
  const [step, setStep] = useState<Step>("details");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [details, setDetails] = useState(() => {
    try { return JSON.parse(localStorage.getItem("isman_customer") || "") as Record<string, string>; } catch { return { name: "", email: "", phone: "", company: "", address: "", city: "Nairobi", notes: "" }; }
  });
  const deliveries = settings.delivery.filter((d) => d.active);
  const payments = useMemo(() => (
    [
      settings.payments.mpesa.active && { id: "mpesa", label: "M-Pesa", icon: "ri-smartphone-line", desc: settings.stk_available ? "We'll send a payment prompt to your phone" : "Pay to our M-Pesa till / paybill" },
      settings.payments.cod.active && { id: "cod", label: settings.payments.cod.label || "Pay on delivery", icon: "ri-hand-coin-line", desc: "Pay when you receive or collect" },
      settings.payments.bank.active && { id: "bank", label: "Bank transfer", icon: "ri-bank-line", desc: "Transfer to our bank account" },
      settings.payments.quote.active && { id: "quote", label: settings.payments.quote.label || "Request a quotation", icon: "ri-file-list-3-line", desc: "Get an official quote / invoice first" },
    ].filter(Boolean) as { id: string; label: string; icon: string; desc: string }[]
  ), [settings]);
  const [delivery, setDelivery] = useState("");
  const [payment, setPayment] = useState("");
  const [placed, setPlaced] = useState<Placed | null>(null);
  const [code, setCode] = useState("");
  const [codeMsg, setCodeMsg] = useState("");

  useEffect(() => {
    if (isOpen) { document.body.style.overflow = "hidden"; if (step === "done") return; setStep("details"); setError(""); }
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!delivery && deliveries[0]) setDelivery(deliveries[0].id); }, [deliveries, delivery]);
  useEffect(() => { if (!payment && payments[0]) setPayment(payments[0].id); }, [payments, payment]);

  const del = deliveries.find((d) => d.id === delivery);
  let delFee = del?.fee ?? 0;
  if (settings.free_delivery_over && subtotal >= settings.free_delivery_over) delFee = 0;
  const rate = settings.vat_rate / 100;
  const vat = settings.prices_include_vat ? Math.round((subtotal + delFee) - (subtotal + delFee) / (1 + rate)) : Math.round((subtotal + delFee) * rate);
  const total = settings.prices_include_vat ? subtotal + delFee : subtotal + delFee + vat;

  const set = (k: string, v: string) => setDetails((d) => ({ ...d, [k]: v }));

  const placeOrder = async () => {
    setBusy(true); setError("");
    try {
      const r = await fetch("/api/store/order.php", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ customer: details, items: cartItems.map((i) => ({ id: i.product.id, qty: i.quantity })), delivery, payment, stk: payment === "mpesa" && settings.stk_available }),
      });
      const j = await r.json().catch(() => ({ ok: false, error: "Network error" }));
      if (!r.ok || !j.ok) throw new Error(j.error || "Could not place order");
      try { localStorage.setItem("isman_customer", JSON.stringify(details)); localStorage.setItem("isman_last_order", JSON.stringify({ ref: j.ref, phone: details.phone })); } catch { /* ignore */ }
      setPlaced({ ref: j.ref, total: j.totals?.total ?? total, instructions: j.instructions, stk: j.stk, phone: details.phone, method: payment });
      trackPurchase(j.ref, Number(j.totals?.total ?? total), settings.currency || "KES", cartItems.map((i) => ({ id: i.product.id, name: i.product.name, qty: i.quantity, price: i.product.price })));
      setStep("done");
      onOrderComplete(j.ref);
    } catch (e) {
      setError((e as Error).message + " — you can also order by WhatsApp or call " + settings.contact_phone);
    } finally { setBusy(false); }
  };

  const sendCode = async () => {
    if (!placed) return;
    setCodeMsg("");
    const r = await fetch("/api/store/track.php", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "mpesa_code", ref: placed.ref, phone: placed.phone, code }) });
    const j = await r.json().catch(() => ({}));
    setCodeMsg(j.ok ? (j.message || "Received") : (j.error || "Could not submit"));
  };

  const close = () => { onClose(); if (step === "done") { setStep("details"); setPlaced(null); setCode(""); setCodeMsg(""); } };

  if (!isOpen) return null;
  const waText = encodeURIComponent(`Hello ISMAN, I'd like to order:\n${cartItems.map((i) => `- ${i.product.name} x${i.quantity}`).join("\n")}\nTotal: ${fmt(total)}`);

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close}></div>
      <div className="relative bg-white rounded-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{step === "done" ? "Order placed" : "Checkout"}</h2>
            {step !== "done" && (
              <div className="flex items-center gap-2 mt-1 text-xs">
                <span className={`font-semibold px-2 py-0.5 rounded-full ${step === "details" ? "bg-teal-600 text-white" : "bg-teal-100 text-teal-700"}`}>1. Your details</span>
                <i className="ri-arrow-right-line text-gray-400"></i>
                <span className={`font-semibold px-2 py-0.5 rounded-full ${step === "delivery" ? "bg-teal-600 text-white" : "bg-gray-100 text-gray-500"}`}>2. Delivery & payment</span>
              </div>
            )}
          </div>
          <button onClick={close} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full cursor-pointer" aria-label="Close"><i className="ri-close-line text-gray-500"></i></button>
        </div>

        <div className="px-6 py-5">
          {step !== "done" && (
            <div className="bg-gray-50 rounded-lg p-4 mb-5">
              <h3 className="text-sm font-bold text-gray-900 mb-2">Order summary ({cartItems.reduce((a, i) => a + i.quantity, 0)} items)</h3>
              <div className="space-y-1.5 max-h-32 overflow-y-auto">
                {cartItems.map((i) => (
                  <div key={i.product.id} className="flex justify-between text-sm text-gray-600">
                    <span className="truncate mr-4">{i.product.name} <span className="text-gray-400">×{i.quantity}</span></span>
                    <span className="font-semibold shrink-0">{fmt(i.product.price * i.quantity)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-gray-200 mt-3 pt-2 space-y-1 text-xs text-gray-500">
                <div className="flex justify-between"><span>Subtotal</span><span>{fmt(subtotal)}</span></div>
                <div className="flex justify-between"><span>Delivery{del ? ` — ${del.label}` : ""}</span><span>{delFee ? fmt(delFee) : "Free"}</span></div>
                <div className="flex justify-between"><span>VAT ({settings.vat_rate}%{settings.prices_include_vat ? ", included" : ""})</span><span>{fmt(vat)}</span></div>
                <div className="flex justify-between text-sm font-bold text-teal-700 pt-1"><span>Total</span><span>{fmt(total)}</span></div>
              </div>
            </div>
          )}

          {step === "details" && (
            <form onSubmit={(e) => { e.preventDefault(); setStep("delivery"); }} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[["name", "Full name *", "text", true], ["phone", "Phone (M-Pesa) *", "tel", true], ["email", "Email (for receipt)", "email", false], ["company", "Company (optional)", "text", false]].map(([k, l, t, req]) => (
                  <label key={k as string} className="block text-xs font-semibold text-gray-700">{l as string}
                    <input required={req as boolean} type={t as string} value={details[k as string] || ""} onChange={(e) => set(k as string, e.target.value)} className="mt-1 w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm font-normal focus:outline-none focus:border-teal-400" />
                  </label>
                ))}
                <label className="block text-xs font-semibold text-gray-700 sm:col-span-2">Delivery address / location
                  <input value={details.address || ""} onChange={(e) => set("address", e.target.value)} placeholder="Building, street, area" className="mt-1 w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm font-normal focus:outline-none focus:border-teal-400" />
                </label>
                <label className="block text-xs font-semibold text-gray-700">Town / city
                  <input value={details.city || ""} onChange={(e) => set("city", e.target.value)} className="mt-1 w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm font-normal focus:outline-none focus:border-teal-400" />
                </label>
                <label className="block text-xs font-semibold text-gray-700 sm:col-span-2">Notes (sizes, site details, preferred time)
                  <textarea rows={2} value={details.notes || ""} onChange={(e) => set("notes", e.target.value)} className="mt-1 w-full border border-gray-200 rounded-md px-3 py-2 text-sm font-normal focus:outline-none focus:border-teal-400" />
                </label>
              </div>
              <button type="submit" className="w-full bg-teal-600 hover:bg-teal-500 text-white font-bold py-3 rounded-lg cursor-pointer">Continue to delivery & payment <i className="ri-arrow-right-line"></i></button>
            </form>
          )}

          {step === "delivery" && (
            <div className="space-y-5">
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-2">Delivery</h3>
                <div className="space-y-2">
                  {deliveries.map((d) => (
                    <label key={d.id} className={`flex items-center justify-between gap-3 border rounded-lg px-4 py-3 cursor-pointer ${delivery === d.id ? "border-teal-500 bg-teal-50" : "border-gray-200"}`}>
                      <span className="flex items-center gap-2 text-sm"><input type="radio" checked={delivery === d.id} onChange={() => setDelivery(d.id)} />{d.label}</span>
                      <span className="text-sm font-semibold">{d.fee && !(settings.free_delivery_over && subtotal >= settings.free_delivery_over) ? fmt(d.fee) : "Free"}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-2">Payment</h3>
                <div className="grid sm:grid-cols-2 gap-2">
                  {payments.map((p) => (
                    <button key={p.id} type="button" onClick={() => setPayment(p.id)} className={`text-left border rounded-lg px-4 py-3 cursor-pointer ${payment === p.id ? "border-teal-500 bg-teal-50" : "border-gray-200 hover:border-teal-300"}`}>
                      <p className="text-sm font-semibold text-gray-900"><i className={`${p.icon} text-teal-600 mr-1`}></i>{p.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{p.desc}</p>
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-gray-400 mt-2"><i className="ri-lock-line"></i> We never ask for card numbers or PINs on this website.</p>
              </div>
              {settings.terms && <p className="text-xs text-gray-500 bg-gray-50 rounded-lg p-3">{settings.terms}</p>}
              {error && <p className="text-sm text-rose-600 bg-rose-50 rounded-lg p-3">{error}</p>}
              <div className="flex gap-3">
                <button onClick={() => setStep("details")} className="px-5 py-3 border border-gray-200 rounded-lg text-sm font-semibold cursor-pointer"><i className="ri-arrow-left-line"></i> Back</button>
                <button onClick={placeOrder} disabled={busy || !delivery || !payment} className="flex-1 bg-teal-600 hover:bg-teal-500 disabled:opacity-60 text-white font-bold py-3 rounded-lg cursor-pointer">
                  {busy ? <><i className="ri-loader-4-line animate-spin inline-block"></i> Placing order…</> : <>Place order — {fmt(total)}</>}
                </button>
              </div>
              <a href={`https://wa.me/${settings.whatsapp}?text=${waText}`} target="_blank" rel="noopener noreferrer" className="block text-center text-xs text-green-700 hover:underline"><i className="ri-whatsapp-line"></i> Prefer WhatsApp? Send this order on WhatsApp instead</a>
            </div>
          )}

          {step === "done" && placed && (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center"><i className="ri-check-double-line text-3xl text-emerald-600"></i></div>
              <h3 className="text-xl font-extrabold text-gray-900 mt-4">Thank you — order received!</h3>
              <p className="text-sm text-gray-500 mt-1">Your order reference</p>
              <p className="text-2xl font-mono font-extrabold text-teal-700 mt-1 tracking-wide">{placed.ref}</p>
              <p className="text-sm text-gray-600 mt-1">Total {fmt(placed.total)}</p>
              {placed.stk && <p className={`mt-4 text-sm rounded-lg p-3 ${placed.stk.ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{placed.stk.ok ? <><i className="ri-smartphone-line"></i> {placed.stk.message} </> : <>M-Pesa prompt could not be sent: {placed.stk.error}</>}</p>}
              <div className="text-left bg-gray-50 rounded-lg p-4 mt-4 text-sm text-gray-700 whitespace-pre-line"><b>Next step:</b> {placed.instructions}</div>
              {placed.method === "mpesa" && (
                <div className="mt-4 text-left">
                  <label className="text-xs font-semibold text-gray-700">Already paid? Enter your M-Pesa confirmation code</label>
                  <div className="flex gap-2 mt-1">
                    <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. SJK3ABCD12" className="flex-1 border border-gray-200 rounded-md px-3 py-2 text-sm font-mono" />
                    <button onClick={sendCode} disabled={code.length < 8} className="bg-teal-600 text-white text-sm font-semibold px-4 rounded-md disabled:opacity-50 cursor-pointer">Submit</button>
                  </div>
                  {codeMsg && <p className="text-xs mt-1 text-teal-700">{codeMsg}</p>}
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-2 mt-6">
                <Link to={`/store/order?ref=${placed.ref}`} onClick={close} className="flex-1 bg-teal-600 hover:bg-teal-500 text-white font-bold py-3 rounded-lg text-sm">Track this order</Link>
                <button onClick={close} className="flex-1 border border-gray-200 py-3 rounded-lg text-sm font-semibold cursor-pointer">Continue shopping</button>
              </div>
              <p className="text-xs text-gray-400 mt-4">A confirmation has been sent to our team{placed ? "" : ""}. Questions? Call {settings.contact_phone}.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
